import {
    Box,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Link,
    Tooltip,
    Typography,
} from '@mui/material';
import { ErrorOutlined, Reviews } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import {
    PriceComparison,
    PriceComparisonDeviationAnomaly,
    PriceComparisonItemAnomaly,
} from '../dto/price-comparison.dto';
import { formatToGermanCurrency, formatToGermanNumber } from 'src/common/toGermanNumber';

export interface BoQComparisonViewProps {
    priceComparison: PriceComparison;
    onMarkError: (offerSubmissionId: string, ordinal: string) => void;
}

export default function BoQComparisonView({ priceComparison, onMarkError }: BoQComparisonViewProps) {
    const { t } = useTranslation();

    const translateDeviationAnomaly = (anomaly: PriceComparisonDeviationAnomaly) => {
        switch (anomaly) {
            case 'LARGE_DEVIATION_NEXT_CHEAPEST_OFFER_SUBMISSION':
                return t('Large deviation to next cheapest offer submission');
            case 'LARGE_DEVIATION_COST_ESTIMATION':
                return t('Large deviation to cost estimation');
            default:
                throw new Error('translateDeviationAnomaly: unknown anomaly type');
        }
    };
    const translateItemAnomaly = (anomaly: PriceComparisonItemAnomaly) => {
        switch (anomaly) {
            case 'PRICE_IS_ZERO':
                return t('Price is zero');
            case 'PRICE_TOO_LOW':
                return t('Price is too low');
            case 'PRICE_TOO_HIGH':
                return t('Price is too high');
            default:
                throw new Error('translateItemAnomaly: unknown anomaly type');
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <TableContainer component={Paper} sx={{ overflow: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow
                            sx={{
                                backgroundColor: 'primary.main',
                                '& .MuiTableCell-root': { color: 'primary.contrastText' },
                            }}
                        >
                            <TableCell>{t('Ordinal')}</TableCell>
                            <TableCell>{t('Short Text')}</TableCell>
                            <TableCell align="right">{t('Cost Estimation')}</TableCell>
                            {priceComparison.offerSubmissions.map(({ id, bidderName }) => (
                                <TableCell key={id} align="right">
                                    {bidderName}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow
                            sx={{
                                '& .MuiTableCell-root': {
                                    fontWeight: 'bold',
                                },
                            }}
                        >
                            <TableCell colSpan={2}>∑ {t('Total Sum')}</TableCell>

                            <TableCell align="right">
                                {formatToGermanCurrency(priceComparison.costEstimation.totalPrice)}
                            </TableCell>
                            {priceComparison.offerSubmissions.map(({ id, totalPrice }) => (
                                <TableCell key={id} align="right">
                                    {formatToGermanCurrency(totalPrice)}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow
                            sx={{
                                '& .MuiTableCell-root': {
                                    fontWeight: 'bold',
                                },
                            }}
                        >
                            <TableCell colSpan={2}>∑ {t('Total Sum After Discount')}</TableCell>
                            <TableCell align="right"></TableCell>

                            {priceComparison.offerSubmissions.map(({ id, totalPriceAfterDiscount }) => (
                                <TableCell key={id} align="right">
                                    {formatToGermanCurrency(totalPriceAfterDiscount)}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow
                            sx={{
                                '& .MuiTableCell-root': {
                                    fontWeight: 'bold',
                                    backgroundColor: 'grey.400',
                                },
                            }}
                        >
                            <TableCell colSpan={3}>Δ {t('Deviation')}</TableCell>

                            {priceComparison.offerSubmissions.map(({ id, deviationToPrevious, deviationAnomaly }) => (
                                <TableCell key={id} align="right">
                                    <Tooltip
                                        title={deviationAnomaly ? translateDeviationAnomaly(deviationAnomaly) : null}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: deviationAnomaly ? 'error.main' : 'inherit',
                                            }}
                                        >
                                            {!isNaN(deviationToPrevious)
                                                ? formatToGermanNumber(deviationToPrevious * 100, 2) + ' %'
                                                : 'n/a'}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow
                            sx={{
                                backgroundColor: 'grey.400',
                                borderBottom: '2px solid',
                                borderBottomColor: 'primary.main',
                            }}
                        >
                            <TableCell colSpan={3}>{t('from')}</TableCell>
                            <TableCell align="right">{t('Cost Estimation')}</TableCell>
                            {priceComparison.offerSubmissions.length > 1 && (
                                <TableCell colSpan={priceComparison.offerSubmissions.length - 1} align="left">
                                    {t('next cheapest offer')}
                                </TableCell>
                            )}
                        </TableRow>

                        {priceComparison.costEstimation.items.map((costEstimationItem, i) => {
                            return (
                                <TableRow
                                    key={costEstimationItem.ordinal}
                                    sx={{
                                        backgroundColor:
                                            costEstimationItem.type === 'HEADER' ? 'grey.300' : 'transparent',
                                        '& .MuiTableCell-root': {
                                            fontWeight: costEstimationItem.type === 'HEADER' ? 'bold' : 'normal',
                                        },
                                    }}
                                >
                                    <TableCell>{costEstimationItem.ordinal}</TableCell>
                                    <TableCell>{costEstimationItem.shortText}</TableCell>
                                    <TableCell align="right">
                                        {formatToGermanCurrency(costEstimationItem.price)}
                                    </TableCell>

                                    {priceComparison.offerSubmissions.map((offerSubmission) => {
                                        const offerSubmissionItem = offerSubmission.items[i];
                                        const hasRechnerischePruefungError =
                                            offerSubmissionItem.assessmentFailReasons['RECHNERISCHE_PRUEFUNG_R2'];
                                        const color = hasRechnerischePruefungError
                                            ? 'warning.main'
                                            : offerSubmissionItem.priceAnnotation === 'HIGHEST_PRICE'
                                              ? 'error.main'
                                              : offerSubmissionItem.priceAnnotation === 'LOWEST_PRICE'
                                                ? 'success.main'
                                                : undefined;

                                        return (
                                            <TableCell key={offerSubmission.id} align="right">
                                                <Link
                                                    sx={{
                                                        ...(color ? { color } : {}),
                                                        textWrap: 'nowrap',
                                                        textDecoration: 'none',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        gap: 0.2,
                                                        '&:hover': {
                                                            textDecoration: 'underline overline',
                                                            color: 'red',
                                                            cursor: 'pointer',
                                                        },
                                                    }}
                                                    onClick={() =>
                                                        onMarkError(offerSubmission.id, costEstimationItem.ordinal)
                                                    }
                                                >
                                                    {hasRechnerischePruefungError && (
                                                        <Reviews sx={{ transform: 'scaleX(-1)' }} />
                                                    )}
                                                    {offerSubmissionItem.anomaly && (
                                                        <Tooltip
                                                            title={translateItemAnomaly(offerSubmissionItem.anomaly)}
                                                        >
                                                            <ErrorOutlined color="primary" />
                                                        </Tooltip>
                                                    )}
                                                    {formatToGermanCurrency(offerSubmissionItem.price)}
                                                </Link>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
